import { useState } from 'react';

import type { Paths } from '@zg-rentals/paths-manifest';

import { Anchor, Banner, CloseButton, TextButton } from '@zillow/constellation-10';
import { getListingAliasFromPath } from '@zg-rentals/string-utils';

import { AdminModal } from '../AdminModal';

import { useBannerVisibility } from '../../hooks/useBannerVisibility';

import type { Brand, User } from '../../types';

const USER_INSPECTOR_HOST_NAME = {
  PRODUCTION: 'https://rental-manager-api.hotpads.com',
  COMET1: 'https://comet1.testpads.net',
};

const LISTING_INSPECTOR_HOST_NAME = {
  PRODUCTION: 'https://listing-tools.hotpads.com',
  COMET1: 'https://comet1.testpads.net',
};

type AdminNavProps = {
  brand: Brand;
  userModel: User | null;
  paths: Paths;
  signInPage: string;
  isDevEnv?: boolean;
  pathname: string;
};

export default function AdminNav(props: AdminNavProps) {
  const { isVisible, handleModalConfirm } = useBannerVisibility();
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);

  const { userModel, brand, signInPage, isDevEnv, pathname } = props;
  const { email, impersonatorEmail, impersonatorUserToken, loggedIn, isPostingPathAdmin } = userModel ?? {};
  const isImpersonator = Boolean(impersonatorEmail && impersonatorUserToken && isPostingPathAdmin);
  const userInspectorHostName = isDevEnv ? USER_INSPECTOR_HOST_NAME.COMET1 : USER_INSPECTOR_HOST_NAME.PRODUCTION;
  const listingInspectorHostName = isDevEnv
    ? LISTING_INSPECTOR_HOST_NAME.COMET1
    : LISTING_INSPECTOR_HOST_NAME.PRODUCTION;
  const inspectUserUrl =
    userInspectorHostName +
    '/rental-manager-api/inspectorTool/userInspectorTool?zgBrand=' +
    brand +
    '&userIdentifier=' +
    email +
    '&submitAction=inspectUser';
  const listingId = getListingAliasFromPath(pathname);

  if (!isPostingPathAdmin) {
    return null;
  }
  // If session expired from 30 minutes of inactivity, while impersonating, we need to redirect user to the login page
  // Locally, this will cause an infinite redirect, so only doing this on __PRODUCTION__
  if (isImpersonator && process.env.NODE_ENV === 'production' && !loggedIn) {
    window.location.assign(signInPage);
  }

  let inspectListingUrl;
  if (!listingId) {
    inspectListingUrl = '';
  } else {
    let listingIdQueryParams;
    if (listingId.includes('_')) {
      const feedListingIdAndFeedId = listingId.split('_');
      listingIdQueryParams = 'feedId=' + feedListingIdAndFeedId[1] + '&feedListingId=' + feedListingIdAndFeedId[0];
    } else {
      listingIdQueryParams = 'aliasEncoded=' + listingId;
    }
    inspectListingUrl =
      listingInspectorHostName +
      '/listing-tools/listing/listingInspectorTool?' +
      listingIdQueryParams +
      '&submitAction=inspectListingFromDatabase';
  }

  return (
    <Banner
      data-testid="AdminNav"
      tone={isImpersonator ? 'critical' : 'warning'}
      isOpen={isVisible}
      actionButton={
        <TextButton asChild onImpact>
          <a href={isImpersonator ? (props.paths.stopImpersonatingPage as string) : props.paths.impersonatePage}>
            {isImpersonator ? 'Stop impersonating' : 'Impersonate user'}
          </a>
        </TextButton>
      }
      closeButton={<CloseButton onImpact title="Hide admin banner" />}
      onClose={() => setIsAdminModalOpen(true)}
    >
      <span id="PostingPathAdminText" data-testid="PostingPathAdminText">
        {isImpersonator ? 'Impersonating' : 'PostingPathAdmin'}
      </span>{' '}
      <Anchor href={inspectUserUrl} target="_blank" rel="noopener noreferrer" onImpact id="Email" data-testid="Email">
        {email}
      </Anchor>
      {listingId && (
        <>
          <Anchor href={inspectListingUrl} target="_blank" rel="noopener noreferrer" onImpact>
            Inspect Listing
          </Anchor>
        </>
      )}
      <AdminModal
        isImpersonator={isImpersonator}
        open={isAdminModalOpen}
        onOpenChange={setIsAdminModalOpen}
        onConfirm={handleModalConfirm}
      />
    </Banner>
  );
}
